import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  AppBar, Toolbar, Typography, Button, IconButton, Menu, MenuItem,
  Box, Container, Avatar, Drawer, List, ListItem, ListItemText,
  ListItemIcon, Divider, useMediaQuery, useTheme, Badge, Tooltip,
} from '@mui/material';
import MenuIcon               from '@mui/icons-material/Menu';
import CloseIcon              from '@mui/icons-material/Close';
import RestaurantIcon         from '@mui/icons-material/Restaurant';
import GroupIcon              from '@mui/icons-material/Group';
import TimeToLeaveIcon        from '@mui/icons-material/TimeToLeave';
import DashboardIcon          from '@mui/icons-material/Dashboard';
import DeliveryDiningIcon     from '@mui/icons-material/DeliveryDining';
import LocalShippingIcon      from '@mui/icons-material/LocalShipping';
import DirectionsCarIcon      from '@mui/icons-material/DirectionsCar';
import TwoWheelerIcon         from '@mui/icons-material/TwoWheeler';
import BusinessCenterIcon     from '@mui/icons-material/BusinessCenter';
import StorefrontIcon         from '@mui/icons-material/Storefront';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import LogoutIcon             from '@mui/icons-material/Logout';
import PersonIcon             from '@mui/icons-material/Person';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import ReceiptLongIcon        from '@mui/icons-material/ReceiptLong';
import { useAuth }            from '../../context/AuthContext';
import realtimeService        from '../../services/realtime.service';

const C = {
  purple:  '#552583',
  gold:    '#FDB927',
  ink:     '#1a1a1a',
  soft:    '#4a4a4a',
  muted:   '#8a8a8a',
  border:  '#ebebeb',
  bg:      '#f6f6f6',
  white:   '#ffffff',
};

const Navbar = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const navigate   = useNavigate();
  const theme      = useTheme();
  const isMobile   = useMediaQuery(theme.breakpoints.down('md'));

  const [anchorEl,      setAnchorEl]      = useState(null);
  const [mobileOpen,    setMobileOpen]    = useState(false);
  const [deliveryOffer, setDeliveryOffer] = useState(null);

  useEffect(() => {
    if (!isAuthenticated || user?.user_type !== 'delivery') return;
    realtimeService.connect();
    const handleOffer    = (data) => { setDeliveryOffer(data); if (navigator.vibrate) navigator.vibrate([300, 100, 300]); };
    const handleOfferGone = () => setDeliveryOffer(null);
    realtimeService.onDeliveryOffer(handleOffer);
    realtimeService.onDeliveryOfferCancelled(handleOfferGone);
    return () => { realtimeService.off('delivery-offer'); realtimeService.off('delivery-offer-cancelled'); };
  }, [isAuthenticated, user?.user_type]);

  const handleMenu      = (e)   => setAnchorEl(e.currentTarget);
  const handleClose     = ()    => setAnchorEl(null);
  const handleLogout    = ()    => { logout(); handleClose(); setMobileOpen(false); navigate('/'); };
  const handleDrawerNav = (path) => { setMobileOpen(false); navigate(path); };

  const isAdmin = user?.user_type === 'admin';

  const navItems = [
    { text: 'Restaurants', icon: <RestaurantIcon />,    path: '/restaurants' },
    { text: 'Logistique',  icon: <LocalShippingIcon />, path: '/logistics',   soon: !isAdmin },
    { text: 'VTC',         icon: <DirectionsCarIcon />, path: '/vtc',         soon: !isAdmin },
    { text: 'Courses',     icon: <TwoWheelerIcon />,    path: '/courses' },
    { text: 'Covoiturage', icon: <GroupIcon />,         path: '/carpool',     soon: !isAdmin },
    { text: 'Location',    icon: <TimeToLeaveIcon />,   path: '/car-rental',  soon: !isAdmin },
  ];

  /* ── Mobile drawer (blanc) ───────────────────────────── */
  const drawer = (
    <Box sx={{ bgcolor: C.white, height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ px: 2.5, pt: 2, pb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `1px solid ${C.border}` }}>
        <Typography sx={{ fontWeight: 900, color: C.ink, letterSpacing: '0.1em', fontSize: '1.05rem' }}>
          USAFIRI
        </Typography>
        <IconButton onClick={() => setMobileOpen(false)} sx={{ color: C.muted, p: 0.5 }}>
          <CloseIcon sx={{ fontSize: '1.2rem' }} />
        </IconButton>
      </Box>

      {isAuthenticated && (
        <Box sx={{ px: 2.5, py: 2, borderBottom: `1px solid ${C.border}` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ width: 36, height: 36, bgcolor: C.purple, fontSize: '0.82rem', fontWeight: 700 }}>
              {user?.first_name?.[0]}{user?.last_name?.[0]}
            </Avatar>
            <Box>
              <Typography sx={{ color: C.ink, fontWeight: 700, fontSize: '0.88rem', lineHeight: 1.2 }}>
                {user?.first_name} {user?.last_name}
              </Typography>
              <Typography sx={{ color: C.muted, fontSize: '0.72rem' }}>{user?.email}</Typography>
            </Box>
          </Box>
        </Box>
      )}

      <List sx={{ flex: 1, px: 1.5, pt: 1.5 }}>
        {navItems.map((item) => (
          <ListItem button key={item.text}
            onClick={() => !item.soon && handleDrawerNav(item.path)}
            sx={{
              borderRadius: '10px', mb: 0.3, py: 0.9, px: 1.5,
              opacity: item.soon ? 0.35 : 1,
              cursor: item.soon ? 'default' : 'pointer',
              '&:hover': { bgcolor: item.soon ? 'transparent' : C.bg },
            }}
          >
            <ListItemIcon sx={{ color: C.soft, minWidth: 36, '& .MuiSvgIcon-root': { fontSize: '1.1rem' } }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText primary={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <span style={{ fontSize: '0.88rem', fontWeight: 500, color: C.ink }}>{item.text}</span>
                {item.soon && (
                  <Box component="span" sx={{ bgcolor: C.bg, color: C.muted, fontSize: '0.56rem', fontWeight: 700, px: 0.7, py: 0.15, borderRadius: '3px', letterSpacing: '0.05em' }}>
                    BIENTÔT
                  </Box>
                )}
              </Box>
            } />
          </ListItem>
        ))}

        <ListItem button onClick={() => handleDrawerNav('/partner/register')} sx={{ borderRadius: '10px', mb: 0.3, py: 0.9, px: 1.5, '&:hover': { bgcolor: C.bg } }}>
          <ListItemIcon sx={{ color: C.purple, minWidth: 36 }}><BusinessCenterIcon sx={{ fontSize: '1.1rem' }} /></ListItemIcon>
          <ListItemText primary={<span style={{ fontSize: '0.88rem', fontWeight: 700, color: C.purple }}>Devenir Partenaire</span>} />
        </ListItem>

        {isAuthenticated && (
          <>
            <Divider sx={{ my: 1.5, borderColor: C.border }} />
            {[
              { label: 'Mes commandes',   icon: <ReceiptLongIcon />, path: '/restaurant/orders' },
              { label: 'Tableau de bord', icon: <DashboardIcon />,   path: '/dashboard' },
              { label: 'Mon profil',      icon: <PersonIcon />,      path: '/profile' },
            ].map(it => (
              <ListItem button key={it.label} onClick={() => handleDrawerNav(it.path)} sx={{ borderRadius: '10px', mb: 0.3, py: 0.9, px: 1.5, '&:hover': { bgcolor: C.bg } }}>
                <ListItemIcon sx={{ color: C.soft, minWidth: 36, '& .MuiSvgIcon-root': { fontSize: '1.1rem' } }}>{it.icon}</ListItemIcon>
                <ListItemText primary={<span style={{ fontSize: '0.88rem', fontWeight: 500, color: C.ink }}>{it.label}</span>} />
              </ListItem>
            ))}
            {(user?.user_type === 'delivery' || isAdmin) && (
              <ListItem button onClick={() => { handleDrawerNav('/partner/delivery'); setDeliveryOffer(null); }} sx={{ borderRadius: '10px', mb: 0.3, py: 0.9, px: 1.5, '&:hover': { bgcolor: C.bg } }}>
                <ListItemIcon sx={{ color: deliveryOffer ? C.gold : C.soft, minWidth: 36 }}>
                  <Badge variant="dot" color="error" invisible={!deliveryOffer}><DeliveryDiningIcon sx={{ fontSize: '1.1rem' }} /></Badge>
                </ListItemIcon>
                <ListItemText primary={<span style={{ fontSize: '0.88rem', fontWeight: deliveryOffer ? 700 : 500, color: deliveryOffer ? C.gold : C.ink }}>Mode Livreur</span>} />
              </ListItem>
            )}
            {(user?.user_type === 'restaurant_owner' || isAdmin) && (
              <ListItem button onClick={() => handleDrawerNav('/partner/restaurant')} sx={{ borderRadius: '10px', mb: 0.3, py: 0.9, px: 1.5, '&:hover': { bgcolor: C.bg } }}>
                <ListItemIcon sx={{ color: C.soft, minWidth: 36 }}><StorefrontIcon sx={{ fontSize: '1.1rem' }} /></ListItemIcon>
                <ListItemText primary={<span style={{ fontSize: '0.88rem', fontWeight: 500, color: C.ink }}>Mon Restaurant</span>} />
              </ListItem>
            )}
            {(user?.user_type === 'logistics' || isAdmin) && (
              <ListItem button onClick={() => handleDrawerNav('/logistics/orders')} sx={{ borderRadius: '10px', mb: 0.3, py: 0.9, px: 1.5, '&:hover': { bgcolor: C.bg } }}>
                <ListItemIcon sx={{ color: C.soft, minWidth: 36 }}><LocalShippingIcon sx={{ fontSize: '1.1rem' }} /></ListItemIcon>
                <ListItemText primary={<span style={{ fontSize: '0.88rem', fontWeight: 500, color: C.ink }}>Mode Transporteur</span>} />
              </ListItem>
            )}
            {(user?.user_type === 'vtc_driver' || isAdmin) && (
              <ListItem button onClick={() => handleDrawerNav('/partner/vtc')} sx={{ borderRadius: '10px', mb: 0.3, py: 0.9, px: 1.5, '&:hover': { bgcolor: C.bg } }}>
                <ListItemIcon sx={{ color: C.soft, minWidth: 36 }}><DirectionsCarIcon sx={{ fontSize: '1.1rem' }} /></ListItemIcon>
                <ListItemText primary={<span style={{ fontSize: '0.88rem', fontWeight: 500, color: C.ink }}>Mode Chauffeur</span>} />
              </ListItem>
            )}
            {(user?.user_type === 'store' || isAdmin) && (
              <ListItem button onClick={() => handleDrawerNav('/partner/store')} sx={{ borderRadius: '10px', mb: 0.3, py: 0.9, px: 1.5, '&:hover': { bgcolor: C.bg } }}>
                <ListItemIcon sx={{ color: C.soft, minWidth: 36 }}><TwoWheelerIcon sx={{ fontSize: '1.1rem' }} /></ListItemIcon>
                <ListItemText primary={<span style={{ fontSize: '0.88rem', fontWeight: 500, color: C.ink }}>Mode Vendeur</span>} />
              </ListItem>
            )}
            {(user?.user_type === 'car_rental' || isAdmin) && (
              <ListItem button onClick={() => handleDrawerNav('/partner/car-rental')} sx={{ borderRadius: '10px', mb: 0.3, py: 0.9, px: 1.5, '&:hover': { bgcolor: C.bg } }}>
                <ListItemIcon sx={{ color: C.soft, minWidth: 36 }}><TimeToLeaveIcon sx={{ fontSize: '1.1rem' }} /></ListItemIcon>
                <ListItemText primary={<span style={{ fontSize: '0.88rem', fontWeight: 500, color: C.ink }}>Mode Location</span>} />
              </ListItem>
            )}
            {isAdmin && (
              <ListItem button onClick={() => handleDrawerNav('/admin/dashboard')} sx={{ borderRadius: '10px', mb: 0.3, py: 0.9, px: 1.5, '&:hover': { bgcolor: C.bg } }}>
                <ListItemIcon sx={{ color: C.soft, minWidth: 36 }}><AdminPanelSettingsIcon sx={{ fontSize: '1.1rem' }} /></ListItemIcon>
                <ListItemText primary={<span style={{ fontSize: '0.88rem', fontWeight: 500, color: C.ink }}>Administration</span>} />
              </ListItem>
            )}
          </>
        )}
      </List>

      {/* Footer */}
      <Box sx={{ px: 2, py: 2, borderTop: `1px solid ${C.border}` }}>
        {isAuthenticated ? (
          <Button fullWidth onClick={handleLogout} startIcon={<LogoutIcon sx={{ fontSize: '1rem !important' }} />}
            sx={{ color: C.soft, borderRadius: '10px', py: 1, border: `1px solid ${C.border}`, textTransform: 'none', fontWeight: 500, fontSize: '0.85rem', '&:hover': { bgcolor: C.bg } }}>
            Déconnexion
          </Button>
        ) : (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button fullWidth onClick={() => handleDrawerNav('/login')}
              sx={{ color: C.ink, borderRadius: '10px', py: 0.9, border: `1px solid ${C.border}`, textTransform: 'none', fontWeight: 600, fontSize: '0.85rem', '&:hover': { bgcolor: C.bg } }}>
              Connexion
            </Button>
            <Button fullWidth onClick={() => handleDrawerNav('/register')}
              sx={{ bgcolor: C.ink, color: 'white', borderRadius: '10px', py: 0.9, fontWeight: 700, fontSize: '0.85rem', textTransform: 'none', '&:hover': { bgcolor: C.soft } }}>
              Inscription
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );

  return (
    <>
      <AppBar position="sticky" elevation={0} sx={{ bgcolor: C.purple, borderBottom: 'none' }}>
        <Container maxWidth="xl">
          <Toolbar disableGutters sx={{ minHeight: { xs: 56, md: 60 } }}>

            {/* Hamburger mobile */}
            {isMobile && (
              <IconButton edge="start" onClick={() => setMobileOpen(true)} sx={{ mr: 1, color: 'rgba(255,255,255,0.8)', p: 0.8 }}>
                <MenuIcon sx={{ fontSize: '1.4rem' }} />
              </IconButton>
            )}

            {/* Logo */}
            <Typography component={Link} to="/"
              sx={{ mr: 5, fontWeight: 900, color: 'white', textDecoration: 'none', letterSpacing: '0.1em', fontSize: { xs: '1.1rem', sm: '1.2rem' }, flexGrow: isMobile ? 1 : 0, '&:hover': { opacity: 0.8 } }}>
              USAFIRI
            </Typography>

            {/* Nav desktop */}
            {!isMobile && (
              <Box sx={{ flexGrow: 1, display: 'flex', gap: 0.3, alignItems: 'center' }}>
                {navItems.map((item) =>
                  item.soon ? (
                    <Tooltip key={item.text} title="Bientôt disponible" arrow>
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', position: 'relative' }}>
                        <Button disabled sx={{ color: 'rgba(255,255,255,0.28) !important', textTransform: 'none', fontSize: '0.88rem', fontWeight: 500, minWidth: 'auto', px: 1.5 }}>
                          {item.text}
                        </Button>
                        <Box sx={{ position: 'absolute', top: 5, right: -4, bgcolor: C.bg, color: C.muted, fontSize: '0.5rem', fontWeight: 700, px: 0.5, py: 0.1, borderRadius: '3px', letterSpacing: '0.04em' }}>
                          SOON
                        </Box>
                      </Box>
                    </Tooltip>
                  ) : (
                    <Button key={item.text} component={Link} to={item.path}
                      sx={{ color: 'rgba(255,255,255,0.75)', textTransform: 'none', fontSize: '0.88rem', fontWeight: 500, minWidth: 'auto', px: 1.5, borderRadius: '8px', '&:hover': { color: 'white', bgcolor: 'rgba(255,255,255,0.08)' } }}>
                      {item.text}
                    </Button>
                  )
                )}
              </Box>
            )}

            {/* Partner CTA desktop */}
            {!isMobile && (
              <Button component={Link} to="/partner/register"
                sx={{ mr: 2.5, border: '1.5px solid rgba(253,185,39,0.5)', color: C.gold, borderRadius: '10px', fontWeight: 600, fontSize: '0.82rem', textTransform: 'none', px: 2, py: 0.7, '&:hover': { bgcolor: C.gold, color: C.purple, borderColor: C.gold } }}>
                Devenir Partenaire
              </Button>
            )}

            {/* Auth desktop */}
            {!isMobile && (
              isAuthenticated ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {user?.user_type === 'delivery' && (
                    <Tooltip title={deliveryOffer ? 'Nouvelle offre !' : 'Mode Livreur'}>
                      <IconButton onClick={() => { navigate('/partner/delivery'); setDeliveryOffer(null); }} sx={{ color: deliveryOffer ? C.gold : C.soft, p: 0.8, '&:hover': { bgcolor: C.bg } }}>
                        <Badge variant="dot" color="error" invisible={!deliveryOffer}><DeliveryDiningIcon sx={{ fontSize: '1.3rem' }} /></Badge>
                      </IconButton>
                    </Tooltip>
                  )}
                  {user?.user_type === 'restaurant_owner' && (
                    <Tooltip title="Mon Restaurant">
                      <IconButton component={Link} to="/partner/restaurant" sx={{ color: C.soft, p: 0.8, '&:hover': { bgcolor: C.bg } }}>
                        <StorefrontIcon sx={{ fontSize: '1.2rem' }} />
                      </IconButton>
                    </Tooltip>
                  )}
                  <IconButton component={Link} to="/dashboard" sx={{ color: C.soft, p: 0.8, '&:hover': { bgcolor: C.bg } }}>
                    <DashboardIcon sx={{ fontSize: '1.2rem' }} />
                  </IconButton>

                  <IconButton onClick={handleMenu} sx={{ ml: 0.3, p: 0.4 }}>
                    <Avatar src={user?.profile_image} sx={{ width: 32, height: 32, bgcolor: C.purple, fontSize: '0.75rem', fontWeight: 700 }}>
                      {!user?.profile_image && `${user?.first_name?.[0] || ''}${user?.last_name?.[0] || ''}`}
                    </Avatar>
                  </IconButton>

                  <Menu anchorEl={anchorEl} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    keepMounted transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    open={Boolean(anchorEl)} onClose={handleClose}
                    PaperProps={{ sx: { bgcolor: C.white, mt: 0.8, border: `1px solid ${C.border}`, borderRadius: '14px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', minWidth: 200, '& .MuiMenuItem-root': { fontSize: '0.85rem', fontWeight: 500, py: 0.9, px: 2, borderRadius: '8px', mx: 0.5, '&:hover': { bgcolor: C.bg } } } }}>
                    <Box sx={{ px: 2, py: 1.2, borderBottom: `1px solid ${C.border}`, mb: 0.5 }}>
                      <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: C.ink }}>{user?.first_name} {user?.last_name}</Typography>
                      <Typography sx={{ fontSize: '0.7rem', color: C.muted, mt: 0.2 }}>{user?.email}</Typography>
                    </Box>
                    <MenuItem onClick={() => { handleClose(); navigate('/profile'); }}>Mon Profil</MenuItem>
                    <MenuItem onClick={() => { handleClose(); navigate('/restaurant/orders'); }}>Mes commandes</MenuItem>
                    <MenuItem onClick={() => { handleClose(); navigate('/dashboard'); }}>Tableau de bord</MenuItem>
                    {(user?.user_type === 'delivery' || isAdmin) && (
                      <MenuItem onClick={() => { handleClose(); navigate('/partner/delivery'); setDeliveryOffer(null); }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                          <Badge variant="dot" color="error" invisible={!deliveryOffer}><DeliveryDiningIcon sx={{ fontSize: '1rem', color: C.muted }} /></Badge>
                          Mode Livreur
                          {deliveryOffer && <NotificationsActiveIcon sx={{ fontSize: 13, color: C.gold, ml: 'auto' }} />}
                        </Box>
                      </MenuItem>
                    )}
                    {(user?.user_type === 'restaurant_owner' || isAdmin) && <MenuItem onClick={() => { handleClose(); navigate('/partner/restaurant'); }}>Mon Restaurant</MenuItem>}
                    {(user?.user_type === 'logistics' || isAdmin) && <MenuItem onClick={() => { handleClose(); navigate('/logistics/orders'); }}>Mode Transporteur</MenuItem>}
                    {(user?.user_type === 'vtc_driver' || isAdmin) && <MenuItem onClick={() => { handleClose(); navigate('/partner/vtc'); }}>Mode Chauffeur</MenuItem>}
                    {(user?.user_type === 'store' || isAdmin) && <MenuItem onClick={() => { handleClose(); navigate('/partner/store'); }}>Mode Vendeur</MenuItem>}
                    {(user?.user_type === 'car_rental' || isAdmin) && <MenuItem onClick={() => { handleClose(); navigate('/partner/car-rental'); }}>Mode Location</MenuItem>}
                    {isAdmin && <MenuItem onClick={() => { handleClose(); navigate('/admin/dashboard'); }}>Administration</MenuItem>}
                    <Divider sx={{ my: 0.5, borderColor: C.border }} />
                    <MenuItem onClick={handleLogout} sx={{ color: `${C.muted} !important` }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><LogoutIcon sx={{ fontSize: '1rem' }} /> Déconnexion</Box>
                    </MenuItem>
                  </Menu>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Button component={Link} to="/login" sx={{ color: 'rgba(255,255,255,0.75)', textTransform: 'none', fontWeight: 600, fontSize: '0.85rem', borderRadius: '10px', px: 2, '&:hover': { color: 'white', bgcolor: 'rgba(255,255,255,0.08)' } }}>
                    Connexion
                  </Button>
                  <Button component={Link} to="/register" sx={{ bgcolor: C.gold, color: C.purple, textTransform: 'none', fontWeight: 700, fontSize: '0.85rem', borderRadius: '10px', px: 2.5, py: 0.7, '&:hover': { bgcolor: '#fdc840' } }}>
                    Inscription
                  </Button>
                </Box>
              )
            )}

            {/* Mobile : avatar ou connexion */}
            {isMobile && (
              isAuthenticated ? (
                <IconButton onClick={() => setMobileOpen(true)} sx={{ p: 0.3 }}>
                  <Avatar sx={{ width: 32, height: 32, bgcolor: C.purple, fontSize: '0.75rem', fontWeight: 700 }}>
                    {user?.first_name?.[0]}{user?.last_name?.[0]}
                  </Avatar>
                </IconButton>
              ) : (
                <Button component={Link} to="/login" sx={{ color: 'white', border: '1px solid rgba(255,255,255,0.3)', borderRadius: '10px', px: 2, py: 0.5, fontSize: '0.82rem', fontWeight: 600, textTransform: 'none', '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' } }}>
                  Connexion
                </Button>
              )
            )}
          </Toolbar>
        </Container>
      </AppBar>

      <Drawer variant="temporary" open={mobileOpen} onClose={() => setMobileOpen(false)} ModalProps={{ keepMounted: true }}
        sx={{ display: { xs: 'block', md: 'none' }, '& .MuiDrawer-paper': { width: 280, boxSizing: 'border-box', borderRight: 'none' }, '& .MuiBackdrop-root': { bgcolor: 'rgba(0,0,0,0.3)' } }}>
        {drawer}
      </Drawer>
    </>
  );
};

export default Navbar;
